import { Skeleton } from '@r/components/ui/skeleton.tsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@r/components/ui/table.tsx';

interface ApiTokenDataTableSkeletonProps {
  /**
   * The number of columns in the table.
   * @type number
   */
  columnCount: number;

  /**
   * The number of rows in the table.
   * @default 10
   * @type number | undefined
   */
  rowCount?: number;

  /**
   * The width of each cell in the table.
   * The length of the array should be equal to the columnCount.
   * Any valid CSS width value is accepted.
   * @default ["auto"]
   * @type string[] | undefined
   */
  cellWidths?: string[];

  /**
   * Flag to prevent the table from shrinking to fit the content.
   * @default false
   * @type boolean | undefined
   */
  shrinkZero?: boolean;
}

export function ApiTokenDataTableSkeleton({
  columnCount,
  rowCount = 10,
  cellWidths = ['auto'],
  shrinkZero = false
}: ApiTokenDataTableSkeletonProps) {
  return (
    <div className="overflow-auto space-y-6 w-full">
      <Skeleton className="w-3/12 h-10" />

      <div className="space-y-3 w-full">
        <Skeleton className="w-7/12 h-6" />
        <Skeleton className="w-4/12 h-6" />
      </div>

      <div className="flex justify-between w-full">
        <Skeleton className="w-80 h-6" />
        <Skeleton className="w-20 h-6" />
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {Array.from({ length: 1 }).map((_, i) => (
              <TableRow key={i} className="hover:bg-transparent">
                {Array.from({ length: columnCount }).map((_, j) => (
                  <TableHead
                    key={j}
                    style={{
                      width: cellWidths[j],
                      minWidth: shrinkZero ? cellWidths[j] : 'auto'
                    }}
                  >
                    <Skeleton className="w-8/12 h-6" />
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {Array.from({ length: rowCount }).map((_, i) => (
              <TableRow key={i} className="hover:bg-transparent">
                {Array.from({ length: columnCount }).map((_, j) => (
                  <TableCell
                    key={j}
                    style={{
                      width: cellWidths[j],
                      minWidth: shrinkZero ? cellWidths[j] : 'auto'
                    }}
                  >
                    <Skeleton className="w-6/12 h-6" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-end w-full">
        <Skeleton className="h-[41px] w-40 rounded-full" />
      </div>
    </div>
  );
}
